import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface UseModalA11yOptions {
  isOpen: boolean;
  onClose: () => void;
  /** Prefer focusing this selector inside the dialog (e.g. close button). */
  initialFocusSelector?: string;
}

/**
 * Escape to close, focus trap, and restore focus when a modal opens/closes.
 * Attach `containerRef` to the dialog element (role="dialog").
 */
export function useModalA11y({
  isOpen,
  onClose,
  initialFocusSelector = '.modal-close-button',
}: UseModalA11yOptions): RefObject<HTMLDivElement> {
  const containerRef = useRef<HTMLDivElement>(null!);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !containerRef.current) return;

      const focusable = containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    requestAnimationFrame(() => {
      const preferred = containerRef.current?.querySelector<HTMLElement>(initialFocusSelector);
      if (preferred) {
        preferred.focus();
        return;
      }
      const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusable?.[0]?.focus();
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
    // onClose is read via ref so unstable callbacks (e.g. inline handlers) do not
    // re-run initial focus / restore focus on every parent re-render.
  }, [isOpen, initialFocusSelector]);

  return containerRef;
}
